import React from 'react';
import { User, Car, MapPin, Phone, FileText, Copy, Trash2, GitBranch } from 'lucide-react';
import { NODE_TYPES } from '../types';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onAddNode: (type: keyof typeof NODE_TYPES, position: { x: number; y: number }) => void;
  onDuplicateSelected?: () => void;
  onDeleteSelected?: () => void;
  onApplyLayout: () => void;
  hasSelection: boolean;
  canvasPosition: { x: number; y: number };
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  onClose,
  onAddNode,
  onDuplicateSelected,
  onDeleteSelected,
  onApplyLayout,
  hasSelection,
  canvasPosition
}) => {
  const nodeTypes = [
    { type: 'person' as const, icon: <User size={16} />, label: 'Person' },
    { type: 'vehicle' as const, icon: <Car size={16} />, label: 'Vehicle' },
    { type: 'location' as const, icon: <MapPin size={16} />, label: 'Location' },
    { type: 'phone' as const, icon: <Phone size={16} />, label: 'Phone' },
    { type: 'generic' as const, icon: <FileText size={16} />, label: 'Generic' }
  ];

  const handleAddNode = (type: keyof typeof NODE_TYPES) => {
    onAddNode(type, canvasPosition);
    onClose();
  };

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };
  
  // Get theme from the parent wrapper
  const theme = document.querySelector('[data-theme]')?.getAttribute('data-theme') || 'dark';

  return (
    <>
      {/* Backdrop to close menu */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        onContextMenu={(e) => e.preventDefault()}
      />
      
      {/* Context Menu */}
      <div
        className={`
          fixed z-50 rounded-lg shadow-xl py-2 min-w-48 transition-colors duration-200
          ${theme === 'light' 
            ? 'bg-white border border-gray-300' 
            : 'bg-gray-800 border border-gray-700'
          }
        `}
        style={{ left: x, top: y }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Add Entity Section */}
        <div className={`
          px-3 py-1 text-xs font-medium uppercase tracking-wide transition-colors duration-200
          ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
        `}>
          Add Entity
        </div>
        {nodeTypes.map((nodeType) => (
          <button
            key={nodeType.type}
            onClick={() => handleAddNode(nodeType.type)}
            className={`
              w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-200
              ${theme === 'light' 
                ? 'text-gray-700 hover:bg-gray-100' 
                : 'text-gray-300 hover:bg-gray-700'
              }
            `}
          >
            <div style={{ color: NODE_TYPES[nodeType.type].color }}>
              {nodeType.icon}
            </div>
            {nodeType.label}
          </button>
        ))}

        {/* Divider */}
        <div className={`
          border-t my-2 transition-colors duration-200
          ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}
        `} />

        {/* Selection Actions */}
        {hasSelection && (
          <>
            <div className={`
              px-3 py-1 text-xs font-medium uppercase tracking-wide transition-colors duration-200
              ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
            `}>
              Selection
            </div>
            {onDuplicateSelected && (
              <button
                onClick={() => handleAction(onDuplicateSelected)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-200
                  ${theme === 'light' 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-gray-300 hover:bg-gray-700'
                  }
                `}
              >
                <Copy size={16} />
                Duplicate
              </button>
            )}
            {onDeleteSelected && (
              <button
                onClick={() => handleAction(onDeleteSelected)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-200
                  ${theme === 'light' 
                    ? 'text-red-600 hover:bg-red-50' 
                    : 'text-red-400 hover:bg-gray-700'
                  }
                `}
              >
                <Trash2 size={16} />
                Delete
              </button>
            )}
            <div className={`
              border-t my-2 transition-colors duration-200
              ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}
            `} />
          </>
        )}

        {/* Layout Actions */}
        <div className={`
          px-3 py-1 text-xs font-medium uppercase tracking-wide transition-colors duration-200
          ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
        `}>
          Layout
        </div>
        <button
          onClick={() => handleAction(onApplyLayout)}
          className={`
            w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-200
            ${theme === 'light' 
              ? 'text-gray-700 hover:bg-gray-100' 
              : 'text-gray-300 hover:bg-gray-700'
            }
          `}
        >
          <GitBranch size={16} />
          Auto Arrange
        </button>
      </div>
    </>
  );
};

export default ContextMenu;
